import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { MessageCircle, Send, ArrowLeft, Search, Check, CheckCheck, Image, Clock, User, BadgeCheck, Loader2, ImagePlus, X, ArrowLeftRight, Package, CheckCircle, XCircle, RotateCcw, ZoomIn, Wallet, Plus, Bell, BellOff, Volume2, VolumeX, Archive, Inbox, Languages } from 'lucide-react';
import axios from '../lib/axios';
import { useAuth, API, useLanguage, useWebSocket } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { DashboardContainer, PageHeader } from '../components/DashboardLayout';
import { formatPrice } from '../utils/currencyFormatter';
import { toast } from 'sonner';

// Check if we're on mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// Skeleton for conversation list in sidebar
const ConversationListSkeleton = () => (
  <div className="space-y-1">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="p-4 flex items-start gap-3 border-b border-border/50 animate-pulse">
        {/* Listing image placeholder with user avatar overlay */}
        <div className="relative flex-shrink-0">
          <div className="w-12 h-12 rounded-lg bg-secondary" />
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-muted ring-2 ring-background" />
        </div>
        {/* Content placeholders */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="h-4 bg-secondary rounded w-3/4" />
            <div className="h-3 bg-secondary rounded w-12" />
          </div>
          <div className="h-3 bg-secondary/70 rounded w-1/2" />
          <div className="h-3 bg-secondary/50 rounded w-2/3" />
        </div>
      </div>
    ))}
  </div>
);

// Skeleton for chat area when loading a conversation
const ChatSkeleton = () => (
  <div className="flex-1 flex flex-col h-full">
    {/* Header skeleton - Product Card */}
    <div className="border-b border-border">
      <div className="px-4 py-2 bg-secondary/50 border-b border-border/50 animate-pulse">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-secondary" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-secondary rounded w-48" />
            <div className="h-3 bg-primary/30 rounded w-20" />
          </div>
          <div className="h-3 bg-secondary rounded w-16" />
        </div>
      </div>
      {/* User info row */}
      <div className="p-3 flex items-center gap-3 animate-pulse">
        <div className="w-9 h-9 rounded-full bg-secondary" />
        <div className="flex-1 space-y-1.5">
          <div className="h-4 bg-secondary rounded w-28" />
          <div className="h-3 bg-secondary/70 rounded w-16" />
        </div>
      </div>
    </div>
    
    {/* Messages skeleton */}
    <div className="flex-1 p-4 space-y-4 animate-pulse">
      {/* Left message (other user) */}
      <div className="flex justify-start">
        <div className="w-[60%] h-12 bg-secondary rounded-2xl rounded-bl-md" />
      </div>
      {/* Right message (you) */}
      <div className="flex justify-end">
        <div className="w-[50%] h-10 bg-primary/30 rounded-2xl rounded-br-md" />
      </div>
      {/* Left message */}
      <div className="flex justify-start">
        <div className="w-[45%] h-16 bg-secondary rounded-2xl rounded-bl-md" />
      </div>
      {/* Right message */}
      <div className="flex justify-end">
        <div className="w-[30%] h-10 bg-primary/30 rounded-2xl rounded-br-md" />
      </div>
      {/* Left message */}
      <div className="flex justify-start">
        <div className="w-[70%] h-14 bg-secondary rounded-2xl rounded-bl-md" />
      </div>
      {/* Right message */}
      <div className="flex justify-end">
        <div className="w-[55%] h-12 bg-primary/30 rounded-2xl rounded-br-md" />
      </div>
    </div>
    
    {/* Input skeleton */}
    <div className="p-4 border-t border-border animate-pulse">
      <div className="flex items-center gap-2">
        <div className="w-12 h-12 rounded-xl bg-secondary" />
        <div className="flex-1 h-12 rounded-xl bg-secondary" />
        <div className="w-12 h-12 rounded-xl bg-primary/30" />
      </div>
    </div>
  </div>
);

// Mobile chat skeleton
const MobileChatSkeleton = () => (
  <div className="fixed inset-0 z-[10000] bg-background flex flex-col h-[100dvh]">
    {/* Header skeleton */}
    <div className="flex-shrink-0 border-b border-border bg-card safe-area-top">
      {/* Product card */}
      <div className="px-3 py-2 flex items-center gap-2 bg-secondary/50 border-b border-border/50 animate-pulse">
        <div className="w-10 h-10 rounded-lg bg-secondary" />
        <div className="flex-1 space-y-1.5">
          <div className="h-4 bg-secondary rounded w-36" />
          <div className="h-3 bg-primary/30 rounded w-16" />
        </div>
        <div className="w-4 h-4 bg-secondary rounded" />
      </div>
      {/* User row */}
      <div className="px-3 py-2 flex items-center gap-3 animate-pulse">
        <div className="w-10 h-10 rounded-xl bg-secondary" />
        <div className="w-9 h-9 rounded-full bg-secondary" />
        <div className="flex-1 space-y-1.5">
          <div className="h-4 bg-secondary rounded w-24" />
          <div className="h-3 bg-secondary/70 rounded w-12" />
        </div>
      </div>
    </div>
    
    {/* Messages skeleton */}
    <div className="flex-1 p-4 space-y-4 animate-pulse">
      <div className="flex justify-start">
        <div className="w-[65%] h-12 bg-secondary rounded-2xl rounded-bl-md" />
      </div>
      <div className="flex justify-end">
        <div className="w-[55%] h-10 bg-primary/30 rounded-2xl rounded-br-md" />
      </div>
      <div className="flex justify-start">
        <div className="w-[50%] h-14 bg-secondary rounded-2xl rounded-bl-md" />
      </div>
      <div className="flex justify-end">
        <div className="w-[40%] h-10 bg-primary/30 rounded-2xl rounded-br-md" />
      </div>
      <div className="flex justify-start">
        <div className="w-[70%] h-12 bg-secondary rounded-2xl rounded-bl-md" />
      </div>
    </div>
    
    {/* Input skeleton */}
    <div className="flex-shrink-0 px-4 pt-3 pb-8 border-t border-border bg-card animate-pulse">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-xl bg-secondary" />
        <div className="flex-1 h-10 rounded-xl bg-secondary" />
        <div className="w-10 h-10 rounded-xl bg-primary/30" />
      </div>
    </div>
  </div>
);

const MessagesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const { t, language } = useLanguage();
  const { 
    lastMessage, 
    isConnected, 
    sendTypingIndicator, 
    sendReadReceipt, 
    typingUsers, 
    setUnreadCount,
    notificationsEnabled,
    soundEnabled,
    requestNotificationPermission,
    toggleSound,
    setActiveConversationId
  } = useWebSocket();
  const isMobile = useIsMobile();
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingChat, setLoadingChat] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [conversationInfo, setConversationInfo] = useState(null);
  const [chatPhoto, setChatPhoto] = useState(null);
  const [chatPhotoPreview, setChatPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [otherUserOnline, setOtherUserOnline] = useState(false);
  const [imageLightbox, setImageLightbox] = useState(null); // For image lightbox modal
  const [showMobileAttachMenu, setShowMobileAttachMenu] = useState(false); // Mobile attachment menu
  
  // Translation state
  const [translations, setTranslations] = useState({}); // { messageId: { text, source_language, loading } }
  
  // Archive filter state
  const [conversationFilter, setConversationFilter] = useState('inbox'); // 'inbox' | 'archived'
  const [conversationCounts, setConversationCounts] = useState({ inbox: 0, archived: 0, total: 0 });
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const photoInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Auto-open conversation from URL params (only on initial load)
  const initialConversationRef = useRef(false);
  
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (conversationId && conversations.length > 0 && !initialConversationRef.current) {
      const conv = conversations.find(c => c.id === conversationId);
      if (conv) {
        loadConversation(conversationId);
        initialConversationRef.current = true;
        // Clear the URL param so it doesn't interfere with manual navigation
        navigate('/messages', { replace: true });
      }
    }
  }, [searchParams, conversations]);

  // Fetch conversations
  useEffect(() => {
    if (user) {
      fetchConversations();
      // Poll for new messages every 30 seconds (as backup to WebSocket)
      const interval = setInterval(() => {
        if (!isConnected) {
          fetchConversations();
        }
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isConnected]);

  // Show notification banner if not enabled and not dismissed
  useEffect(() => {
    if (user && !notificationsEnabled && 'Notification' in window && Notification.permission !== 'denied') {
      const dismissed = localStorage.getItem('notificationBannerDismissed');
      if (!dismissed) {
        setShowNotificationBanner(true);
      }
    }
  }, [user, notificationsEnabled]);

  // Clear active conversation on unmount
  useEffect(() => {
    return () => {
      if (setActiveConversationId) setActiveConversationId(null);
    };
  }, [setActiveConversationId]);

  // Handle real-time messages from WebSocket
  useEffect(() => {
    if (!lastMessage) return;

    if (lastMessage.type === 'new_message') {
      const msg = lastMessage.message;
      
      // Skip if this is our own message (we already added it optimistically)
      if (msg.sender_id === user?.id) {
        // Just update conversation list, don't add to messages again
        fetchConversations();
        return;
      }
      
      // If we're in the relevant conversation, add the message
      if (selectedConversation) {
        const [listingId, otherUserId] = selectedConversation.split(':');
        const isRelevant = msg.listing_id === listingId && 
          (msg.sender_id === otherUserId || msg.receiver_id === otherUserId);
        
        if (isRelevant) {
          // Check if message already exists (by id or temp_id)
          setMessages(prev => {
            if (prev.find(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          // Send read receipt since we're viewing this conversation
          sendReadReceipt(msg.conversation_id, msg.sender_id);
        }
      }
      
      // Update conversation list
      fetchConversations();
    } else if (lastMessage.type === 'message_sent') {
      // Our own message was sent - replace temp message with real one
      const msg = lastMessage.message;
      if (selectedConversation) {
        setMessages(prev => {
          // Remove temp message and add real one (or just skip if already there)
          const withoutTemp = prev.filter(m => !m.id?.startsWith('temp-') || m.content !== msg.content);
          if (withoutTemp.find(m => m.id === msg.id)) return withoutTemp;
          return [...withoutTemp, msg];
        });
      }
    } else if (lastMessage.type === 'messages_read') {
      // Mark messages as read in UI
      setMessages(prev => prev.map(m => 
        m.sender_id === user?.id && m.receiver_id === lastMessage.reader_id
          ? { ...m, read: true }
          : m
      ));
    }
  }, [lastMessage, selectedConversation, user?.id, sendReadReceipt]);

  // Check if other user is online when loading conversation
  useEffect(() => {
    const checkOnlineStatus = async () => {
      if (conversationInfo?.participant?.id) {
        try {
          const res = await axios.get(`${API}/users/${conversationInfo.participant.id}/online-status`);
          setOtherUserOnline(res.data.is_online);
        } catch (error) {
          // Silent fail
        }
      }
    };
    checkOnlineStatus();
  }, [conversationInfo?.participant?.id]);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (!conversationInfo?.participant?.id) return;
    
    // Send typing indicator
    sendTypingIndicator(conversationInfo.participant.id, true);
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Stop typing indicator after 2 seconds of no typing
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(conversationInfo.participant.id, false);
    }, 2000);
  }, [conversationInfo?.participant?.id, sendTypingIndicator]);

  // Scroll to bottom of messages container (not the page)
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle browser back button on mobile to close conversation instead of leaving page
  useEffect(() => {
    const handlePopState = (e) => {
      if (isMobile && selectedConversation) {
        // Prevent default navigation and close the conversation
        setSelectedConversation(null);
        setConversationInfo(null);
        setMessages([]);
        if (setActiveConversationId) setActiveConversationId(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isMobile, selectedConversation]);

  // Body scroll lock when image lightbox is open
  useEffect(() => {
    if (imageLightbox) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [imageLightbox]);

  const fetchConversations = async (filter = conversationFilter) => {
    try {
      const res = await axios.get(`${API}/messages/conversations?filter=${filter}`);
      setConversations(res.data.conversations);
      setConversationCounts(res.data.counts || { inbox: 0, archived: 0, total: 0 });
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Archive/unarchive conversation
  const toggleArchiveConversation = async (conversationId, isCurrentlyArchived) => {
    const endpoint = isCurrentlyArchived ? 'unarchive' : 'archive';
    
    // Optimistic UI update
    setConversations(prev => prev.filter(c => c.id !== conversationId));
    setConversationCounts(prev => ({
      ...prev,
      inbox: isCurrentlyArchived ? prev.inbox + 1 : prev.inbox - 1,
      archived: isCurrentlyArchived ? prev.archived - 1 : prev.archived + 1,
    }));
    
    try {
      await axios.post(`${API}/messages/conversations/${conversationId}/${endpoint}`);
      toast.success(isCurrentlyArchived ? 'Gesprek hersteld' : 'Gesprek gearchiveerd');
    } catch (error) {
      console.error(`Failed to ${endpoint} conversation:`, error);
      toast.error(`Kon gesprek niet ${isCurrentlyArchived ? 'herstellen' : 'archiveren'}`);
      // Revert on error
      fetchConversations();
    }
  };

  // Refetch when filter changes
  useEffect(() => {
    if (user) {
      fetchConversations(conversationFilter);
    }
  }, [conversationFilter]);

  const loadConversation = async (conversationId, silent = false) => {
    setSelectedConversation(conversationId);
    if (!silent) {
      setLoadingChat(true);
      setConversationInfo(null); // Reset to trigger skeleton
      setMessages([]); // Clear old messages - only when switching conversations
    }
    
    // Update active conversation for notification suppression
    // Use the same format as backend: min_max_listing (lexicographic string comparison)
    if (setActiveConversationId) {
      const [listingId, otherUserId] = conversationId.split(':');
      // Build conversation_id in the same format as backend: min_max_listing
      // Backend uses Python min()/max() which does lexicographic comparison on strings
      const sortedIds = [user?.id, otherUserId].sort();
      const fullConversationId = `${sortedIds[0]}_${sortedIds[1]}_${listingId}`;
      setActiveConversationId(fullConversationId);
    }
    
    // Push history state on mobile so system back button closes the conversation
    if (isMobile) {
      window.history.pushState({ conversationOpen: true }, '');
    }
    
    try {
      // conversationId is "listing_id:other_user_id"
      const [listingId, otherUserId] = conversationId.split(':');
      const res = await axios.get(`${API}/messages/conversation/${listingId}/${otherUserId}`);
      
      if (silent) {
        // Silent mode: merge new messages without replacing existing ones
        // This preserves optimistic updates and prevents flicker
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMessages = res.data.messages.filter(m => !existingIds.has(m.id));
          if (newMessages.length === 0) return prev;
          return [...prev, ...newMessages].sort((a, b) => 
            new Date(a.created_at) - new Date(b.created_at)
          );
        });
      } else {
        setMessages(res.data.messages);
      }
      
      setConversationInfo({
        listing: res.data.listing,
        participant: res.data.participant,
      });
      
      // Get the unread count from the conversation list (this is pre-fetched)
      const conversationData = conversations.find(c => c.id === conversationId);
      const unreadInConversation = conversationData?.unread_count || 0;
      
      // Optimistic update: immediately update local conversation unread count
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      ));
      
      // Mark conversation as read on backend
      try {
        const markReadRes = await axios.put(`${API}/messages/conversations/${listingId}/${otherUserId}/read`);
        const messagesMarked = markReadRes.data?.messages_marked || unreadInConversation;
        
        // Update global unread count based on backend response
        if (messagesMarked > 0 && setUnreadCount) {
          setUnreadCount(prev => Math.max(0, prev - messagesMarked));
        }
      } catch (markError) {
        // Fallback: still try to decrease unread count using local data
        if (unreadInConversation > 0 && setUnreadCount) {
          setUnreadCount(prev => Math.max(0, prev - unreadInConversation));
        }
      }
      
      // Scroll to bottom after messages load
      setTimeout(() => scrollToBottom(), 100);
      
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      if (!silent) {
        setLoadingChat(false);
      }
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !chatPhoto) || !selectedConversation || sending) return;

    setSending(true);
    const tempMessageId = 'temp-' + Date.now();
    
    try {
      let imageUrl = null;
      
      // Upload photo if attached
      if (chatPhoto) {
        setUploadingPhoto(true);
        const formData = new FormData();
        formData.append('file', chatPhoto);
        
        try {
          const uploadResponse = await axios.post(`${API}/upload`, formData, {
            headers: { 
              'Content-Type': 'multipart/form-data'
            }
          });
          imageUrl = uploadResponse.data.url;
        } catch (uploadError) {
          console.error('Photo upload failed:', uploadError);
          toast.error('Failed to upload photo');
          setSending(false);
          setUploadingPhoto(false);
          return;
        }
        setUploadingPhoto(false);
      }
      
      const messageContent = newMessage.trim() || (imageUrl ? '📷 Photo' : '');
      
      // Optimistic update
      const tempMessage = {
        id: tempMessageId,
        sender_id: user.id,
        content: messageContent,
        image_url: imageUrl,
        read: false,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, tempMessage]);
      setNewMessage('');
      setChatPhoto(null);
      setChatPhotoPreview(null);

      // Use existing messages endpoint with receiver_id
      await axios.post(`${API}/messages`, {
        listing_id: conversationInfo?.listing?.id,
        receiver_id: conversationInfo?.participant?.id,
        content: messageContent,
        message_type: 'inquiry',
        image_url: imageUrl,
      });

      // WebSocket handles the message_sent event which replaces our temp message
      // with the real one, so no need to reload the entire conversation
      // This prevents the flash/flicker that occurred when messages were cleared
    } catch (error) {
      console.error('Failed to send message:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Provide more specific error messages
      let errorMessage = language === 'nl' ? 'Bericht versturen mislukt' : 
                         language === 'es' ? 'Error al enviar mensaje' : 
                         'Failed to send message';
      
      if (error.response?.status === 403) {
        errorMessage = language === 'nl' ? 'Sessie verlopen, ververs de pagina' :
                       language === 'es' ? 'Sesión expirada, recarga la página' :
                       'Session expired, please refresh';
      } else if (error.response?.status === 401) {
        errorMessage = language === 'nl' ? 'Log opnieuw in om berichten te sturen' :
                       language === 'es' ? 'Inicia sesión para enviar mensajes' :
                       'Please log in again to send messages';
      } else if (!navigator.onLine) {
        errorMessage = language === 'nl' ? 'Geen internetverbinding' :
                       language === 'es' ? 'Sin conexión a internet' :
                       'No internet connection';
      }
      
      toast.error(errorMessage);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempMessageId));
    } finally {
      setSending(false);
    }
  };

  // Translate a message
  const translateMessage = async (messageId) => {
    // Already loading or translated?
    if (translations[messageId]?.loading || translations[messageId]?.text) {
      // If already translated, toggle visibility
      if (translations[messageId]?.text) {
        setTranslations(prev => ({
          ...prev,
          [messageId]: { ...prev[messageId], visible: !prev[messageId]?.visible }
        }));
      }
      return;
    }

    // Set loading state
    setTranslations(prev => ({
      ...prev,
      [messageId]: { loading: true, visible: true }
    }));

    try {
      const response = await axios.post(`${API}/messages/${messageId}/translate?target_lang=${language}`);
      
      setTranslations(prev => ({
        ...prev,
        [messageId]: {
          text: response.data.translated_text,
          source_language: response.data.source_language,
          loading: false,
          visible: true
        }
      }));
    } catch (error) {
      console.error('Translation failed:', error);
      toast.error(t('translation_failed') || 'Translation failed');
      setTranslations(prev => ({
        ...prev,
        [messageId]: { loading: false, error: true, visible: false }
      }));
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be less than 5MB');
        return;
      }
      setChatPhoto(file);
      setChatPhotoPreview(URL.createObjectURL(file));
    }
  };

  const removePhoto = () => {
    setChatPhoto(null);
    setChatPhotoPreview(null);
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const filteredConversations = conversations.filter(conv =>
    conv.participant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.listing_title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnread = conversations.reduce((sum, c) => sum + c.unread_count, 0);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <DashboardContainer>
          <div className="py-16 text-center">
            <MessageCircle className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h1 className="text-2xl font-bold mb-2">Sign in to view messages</h1>
            <p className="text-muted-foreground mb-6">Connect with buyers and sellers</p>
            <button
              onClick={() => navigate('/auth')}
              className="btn-primary px-6 py-3"
            >
              Sign In
            </button>
          </div>
        </DashboardContainer>
        <Footer />
      </div>
    );
  }

  // Mobile conversation view - Full screen dedicated layout
  // Handle closing conversation (also cleans up history state on mobile)
  const closeConversation = () => {
    if (isMobile && window.history.state?.conversationOpen) {
      // Go back in history which will trigger popstate handler
      window.history.back();
    } else {
      setSelectedConversation(null);
      setConversationInfo(null);
      setMessages([]);
    }
  };

  // Mobile loading state - show skeleton
  if (isMobile && selectedConversation && loadingChat) {
    return <MobileChatSkeleton />;
  }

  if (isMobile && selectedConversation && conversationInfo) {
    return (
      <div className="fixed inset-0 z-[10000] bg-background flex flex-col h-[100dvh]" data-testid="mobile-chat-view">
        {/* Mobile Chat Header with Product Context */}
        <div className="flex-shrink-0 border-b border-border bg-card safe-area-top">
          {/* Compact Product Card */}
          <button
            onClick={() => navigate(`/listing/${conversationInfo.listing.id}`)}
            className="w-full px-3 py-2 flex items-center gap-2 bg-secondary/50 border-b border-border/50"
            data-testid="mobile-chat-listing-card"
          >
            {/* Listing Thumbnail */}
            {conversationInfo.listing.image ? (
              <img
                src={conversationInfo.listing.image}
                alt={conversationInfo.listing.title}
                className="w-10 h-10 rounded-lg object-cover bg-secondary flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
            
            {/* Listing Info */}
            <div className="flex-1 min-w-0 text-left">
              <p className="font-medium text-sm text-foreground truncate">
                {conversationInfo.listing.title}
              </p>
              <p className="text-xs text-primary font-semibold">
                {formatPrice(conversationInfo.listing.price, 'USD')}
              </p>
            </div>
            
            {/* Arrow indicator */}
            <ArrowLeft className="w-4 h-4 text-muted-foreground rotate-180 flex-shrink-0" />
          </button>
          
          {/* User Row */}
          <div className="px-3 py-2 flex items-center gap-3">
            <button
              onClick={closeConversation}
              className="p-2 hover:bg-secondary rounded-xl transition-colors"
              data-testid="mobile-chat-back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            {/* User Avatar with Online Status */}
            <div className="relative flex-shrink-0">
              {conversationInfo.participant.avatar ? (
                <img
                  src={conversationInfo.participant.avatar}
                  alt={conversationInfo.participant.name}
                  className="w-9 h-9 rounded-full object-cover"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
              )}
              {otherUserOnline && (
                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full ring-2 ring-card" />
              )}
            </div>
            
            {/* User Name & Status */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-sm text-foreground truncate">
                  {conversationInfo.participant.name}
                </span>
                {conversationInfo.participant.is_verified && (
                  <BadgeCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {typingUsers[conversationInfo.participant.id] ? (
                  <span className="text-primary animate-pulse">typing...</span>
                ) : otherUserOnline ? (
                  <span className="text-green-500">Online</span>
                ) : (
                  'Offline'
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div 
          ref={messagesContainerRef} 
          className="flex-1 overflow-y-auto p-4 min-w-0"
        >
          <div className="flex flex-col gap-4 min-w-0">
          {messages.map((msg, index) => {
            const isOwn = msg.sender_id === user.id;
            const showDate = index === 0 || 
              new Date(msg.created_at).toDateString() !== new Date(messages[index - 1].created_at).toDateString();
            const isTradeProposal = msg.message_type === 'trade_proposal';
            const isTradeResponse = msg.message_type === 'trade_response';
            const isTradeCounter = msg.message_type === 'trade_counter';
            const isTradeAccepted = isTradeResponse && msg.content?.includes('ACCEPTED');
            
            return (
              <div key={msg.id}>
                {showDate && (
                  <div className="text-center my-4">
                    <span className="text-xs text-muted-foreground bg-secondary px-3 py-1 rounded-full">
                      {new Date(msg.created_at).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                )}
                <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} min-w-0`}>
                  {isTradeProposal ? (
                    <div className="max-w-[90%] p-4 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                          <ArrowLeftRight className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-sm">Trade Proposal</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap break-all overflow-hidden text-foreground" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</p>
                      <Link 
                        to="/trade-proposals" 
                        className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-colors"
                      >
                        <Package className="w-4 h-4" />
                        Accept or Decline
                      </Link>
                      <div className="flex items-center justify-end gap-1 mt-2 text-muted-foreground">
                        <span className="text-[10px]">
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ) : isTradeResponse ? (
                    <div className={`max-w-[90%] p-4 rounded-2xl ${
                      isTradeAccepted 
                        ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30'
                        : 'bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          isTradeAccepted 
                            ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                            : 'bg-gradient-to-br from-red-500 to-orange-600'
                        }`}>
                          {isTradeAccepted 
                            ? <CheckCircle className="w-4 h-4 text-white" />
                            : <XCircle className="w-4 h-4 text-white" />
                          }
                        </div>
                        <span className={`font-semibold text-sm ${
                          isTradeAccepted ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {isTradeAccepted ? 'Trade Accepted!' : 'Trade Declined'}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap break-all overflow-hidden text-foreground" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</p>
                      <div className="flex items-center justify-end gap-1 mt-2 text-muted-foreground">
                        <span className="text-[10px]">
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ) : isTradeCounter ? (
                    <div className="max-w-[90%] p-4 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                          <RotateCcw className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-semibold text-amber-600 dark:text-amber-400 text-sm">Counter Offer</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap break-all overflow-hidden text-foreground" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</p>
                      <Link 
                        to="/trade-proposals" 
                        className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
                      >
                        <Package className="w-4 h-4" />
                        View Counter Offer
                      </Link>
                      <div className="flex items-center justify-end gap-1 mt-2 text-muted-foreground">
                        <span className="text-[10px]">
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`max-w-[80%] p-3 rounded-2xl min-w-0 overflow-hidden ${
                        isOwn
                          ? 'bg-primary text-white rounded-br-md'
                          : 'bg-secondary text-foreground rounded-bl-md'
                      }`}
                    >
                      {msg.image_url && (
                        <div 
                          className="relative group mb-2 cursor-pointer"
                          onClick={() => setImageLightbox(msg.image_url)}
                        >
                          <img 
                            src={msg.image_url} 
                            alt="Attached" 
                            className="max-w-full rounded-lg hover:opacity-90 transition-opacity"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg">
                            <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap break-all overflow-hidden" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</p>
                      
                      {/* Translation Section */}
                      {translations[msg.id]?.visible && translations[msg.id]?.text && (
                        <div className={`mt-2 pt-2 border-t ${isOwn ? 'border-white/20' : 'border-border'}`}>
                          <p className={`text-sm whitespace-pre-wrap break-all overflow-hidden italic ${isOwn ? 'text-white/90' : 'text-foreground/90'}`} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                            {translations[msg.id].text}
                          </p>
                          <span className={`text-[9px] ${isOwn ? 'text-white/50' : 'text-muted-foreground'}`}>
                            {t('translated_from') || 'Translated from'} {translations[msg.id].source_language?.toUpperCase()}
                          </span>
                        </div>
                      )}
                      
                      <div className={`flex items-center justify-between gap-2 mt-1 ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`}>
                        {/* Translate Button - Only show for other user's messages */}
                        {!isOwn && !msg.id?.startsWith('temp-') && (
                          <button
                            onClick={() => translateMessage(msg.id)}
                            className={`flex items-center gap-1 text-[10px] hover:opacity-80 transition-opacity ${
                              translations[msg.id]?.visible ? 'opacity-100' : 'opacity-60'
                            }`}
                            title={t('translate') || 'Translate'}
                          >
                            {translations[msg.id]?.loading ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Languages className="w-3 h-3" />
                            )}
                            <span>{translations[msg.id]?.visible ? (t('hide') || 'Hide') : (t('translate') || 'Translate')}</span>
                          </button>
                        )}
                        {isOwn && <div />}
                        
                        <div className="flex items-center gap-1">
                          <span className="text-[10px]">
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {isOwn && (
                            msg.read ? (
                              <CheckCheck className="w-3 h-3" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          </div>
          <div ref={messagesEndRef} />
        </div>

        {/* Mobile Message Input - Fixed at bottom with safe area padding */}
        <form onSubmit={sendMessage} className="flex-shrink-0 px-4 pt-3 pb-8 border-t border-border bg-card">
          {/* Photo Preview */}
          {chatPhotoPreview && (
            <div className="mb-3 relative inline-block">
              <img 
                src={chatPhotoPreview} 
                alt="Preview" 
                className="h-16 w-auto rounded-lg border border-border"
              />
              <button
                type="button"
                onClick={removePhoto}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Attachment Menu Popup */}
          {showMobileAttachMenu && (
            <div className="absolute bottom-20 left-4 right-4 bg-card border border-border rounded-xl shadow-xl p-2 z-10">
              <button
                type="button"
                onClick={() => {
                  photoInputRef.current?.click();
                  setShowMobileAttachMenu(false);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
              >
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <ImagePlus className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-foreground">Photo</p>
                  <p className="text-xs text-muted-foreground">Send an image</p>
                </div>
              </button>
              
              {user?.usdt_erc20_address && (
                <button
                  type="button"
                  onClick={() => {
                    setNewMessage(prev => prev + (prev ? '\n' : '') + `💰 My USDT (ERC20) Address:\n${user.usdt_erc20_address}`);
                    toast.success('USDT address added to message');
                    setShowMobileAttachMenu(false);
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
                >
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-foreground">USDT Address</p>
                    <p className="text-xs text-muted-foreground">Share your wallet address</p>
                  </div>
                </button>
              )}
              
              {/* Close button */}
              <button
                type="button"
                onClick={() => setShowMobileAttachMenu(false)}
                className="w-full mt-2 p-2 text-center text-muted-foreground text-sm hover:bg-secondary rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
          
          <div className="flex items-center gap-2 w-full">
            <input
              type="file"
              ref={photoInputRef}
              onChange={handlePhotoSelect}
              accept="image/*,.heic,.heif"
              className="hidden"
            />
            
            {/* Plus button for attachment menu */}
            <button
              type="button"
              onClick={() => setShowMobileAttachMenu(!showMobileAttachMenu)}
              className={`p-2.5 rounded-xl transition-colors flex-shrink-0 ${
                showMobileAttachMenu 
                  ? 'bg-primary text-white rotate-45' 
                  : 'bg-secondary hover:bg-secondary/80 text-muted-foreground'
              }`}
              data-testid="mobile-attach-menu"
            >
              <Plus className="w-5 h-5 transition-transform" />
            </button>
            
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                handleTyping();
              }}
              onFocus={() => setShowMobileAttachMenu(false)}
              placeholder="Type a message..."
              className="flex-1 min-w-0 px-3 py-2.5 bg-secondary rounded-xl text-sm focus:ring-2 focus:ring-primary/50 outline-none"
              data-testid="mobile-message-input"
            />
            <button
              type="submit"
              disabled={(!newMessage.trim() && !chatPhoto) || sending}
              className="p-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 flex-shrink-0"
              data-testid="mobile-send-message"
            >
              {sending || uploadingPhoto ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </form>

        {/* Image Lightbox Modal for Mobile */}
        {imageLightbox && (
          <div 
            className="fixed inset-0 z-[10002] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={() => setImageLightbox(null)}
            data-testid="image-lightbox-mobile"
          >
            <button 
              onClick={() => setImageLightbox(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
              aria-label="Close"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <img 
              src={imageLightbox} 
              alt="Full size" 
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="messages-page">
      <Navbar />
      
      <DashboardContainer className="pb-24 md:pb-6">
        {/* Standardized Header */}
        <PageHeader pageKey="messages">
          <p className="text-sm text-muted-foreground">
            {totalUnread > 0 ? `${totalUnread} unread` : 'All caught up!'}
          </p>
        </PageHeader>

        <div 
          className="md:bg-card md:rounded-2xl md:border md:border-border md:overflow-hidden" 
          style={isMobile ? {} : { height: 'calc(100vh - 280px)' }}
        >
          <div className={`flex ${isMobile ? '' : 'h-full'}`}>
            {/* Conversations List */}
            <div className={`w-full md:w-96 md:border-r md:border-border flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
              {/* Search */}
              <div className="p-3 md:p-4 md:border-b md:border-border">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-secondary rounded-xl text-sm focus:ring-2 focus:ring-primary/50 outline-none"
                    data-testid="search-conversations"
                  />
                </div>
                
                {/* Inbox/Archive Filter Tabs */}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setConversationFilter('inbox')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      conversationFilter === 'inbox'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid="filter-inbox"
                  >
                    <Inbox className="w-4 h-4" />
                    Inbox
                    {conversationCounts.inbox > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-background/20 rounded text-xs">
                        {conversationCounts.inbox}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setConversationFilter('archived')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      conversationFilter === 'archived'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid="filter-archived"
                  >
                    <Archive className="w-4 h-4" />
                    Archief
                    {conversationCounts.archived > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-background/20 rounded text-xs">
                        {conversationCounts.archived}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Notification Permission Banner */}
              {showNotificationBanner && (
                <div className="mx-3 md:mx-4 mb-3 p-3 bg-primary/10 border border-primary/20 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Bell className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">Stay updated</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Get notified when you receive new messages</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={async () => {
                            const granted = await requestNotificationPermission();
                            if (granted) {
                              toast.success('Notifications enabled!');
                            }
                            setShowNotificationBanner(false);
                          }}
                          className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors"
                          data-testid="enable-notifications-btn"
                        >
                          Enable
                        </button>
                        <button
                          onClick={() => {
                            setShowNotificationBanner(false);
                            localStorage.setItem('notificationBannerDismissed', 'true');
                          }}
                          className="px-3 py-1.5 text-muted-foreground text-xs hover:text-foreground transition-colors"
                          data-testid="dismiss-notifications-btn"
                        >
                          Not now
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowNotificationBanner(false);
                        localStorage.setItem('notificationBannerDismissed', 'true');
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Conversation List */}
              <div className={`${isMobile ? '' : 'flex-1 overflow-y-auto'}`}>
                {loading ? (
                  <ConversationListSkeleton />
                ) : filteredConversations.length === 0 ? (
                  <div className="p-6 text-center">
                    {conversationFilter === 'archived' ? (
                      <>
                        <Archive className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-muted-foreground text-sm">Geen gearchiveerde gesprekken</p>
                        <p className="text-xs text-muted-foreground mt-1">Gesprekken die je archiveert verschijnen hier</p>
                      </>
                    ) : (
                      <>
                        <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-muted-foreground text-sm">Nog geen gesprekken</p>
                        <p className="text-xs text-muted-foreground mt-1">Begin met chatten door een verkoper te berichten</p>
                      </>
                    )}
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <div
                      key={conv.id}
                      className={`group relative w-full p-4 flex items-start gap-3 hover:bg-secondary/50 transition-colors border-b border-border/50 text-left ${
                        selectedConversation === conv.id ? 'bg-secondary' : ''
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      {/* Clickable area for conversation */}
                      <button
                        onClick={() => loadConversation(conv.id)}
                        className="absolute inset-0 z-0"
                        aria-label={`Open conversation about ${conv.listing_title}`}
                      />
                      
                      {/* Product-First Avatar: Listing image with user overlay */}
                      <div className="relative flex-shrink-0 z-10">
                        {/* Primary: Listing Image */}
                        {conv.listing_image ? (
                          <img
                            src={conv.listing_image.url || conv.listing_image}
                            alt={conv.listing_title}
                            className={`w-12 h-12 rounded-lg object-cover bg-secondary ${
                              conv.listing_status === 'deleted' ? 'opacity-50 grayscale' : ''
                            }`}
                          />
                        ) : (
                          <div className={`w-12 h-12 rounded-lg bg-secondary flex items-center justify-center ${
                            conv.listing_status === 'deleted' ? 'opacity-50' : ''
                          }`}>
                            <Package className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                        
                        {/* User Avatar Overlay (bottom-right) */}
                        <div className="absolute -bottom-1 -right-1 ring-2 ring-background rounded-full">
                          {conv.participant.avatar ? (
                            <img
                              src={conv.participant.avatar}
                              alt={conv.participant.name}
                              className="w-5 h-5 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                              <User className="w-3 h-3 text-primary" />
                            </div>
                          )}
                        </div>
                        
                        {/* Unread Badge (top-right) */}
                        {conv.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center z-10">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>

                      {/* Content: Product-first titles */}
                      <div className="flex-1 min-w-0 z-10 pointer-events-none">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          {/* Main Title: Listing Title */}
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`font-medium truncate ${
                              conv.listing_status === 'deleted' 
                                ? 'text-muted-foreground' 
                                : conv.unread_count > 0 ? 'text-foreground' : 'text-foreground/90'
                            }`}>
                              {conv.listing_title || 'Unnamed Listing'}
                            </span>
                            {/* Deleted Badge */}
                            {conv.listing_status === 'deleted' && (
                              <span className="flex-shrink-0 px-1.5 py-0.5 bg-destructive/10 text-destructive text-[10px] font-medium rounded">
                                Verwijderd
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {formatTime(conv.last_message.created_at)}
                          </span>
                        </div>
                        {/* Subtitle: User Name */}
                        <p className="text-xs text-muted-foreground truncate mb-1 flex items-center gap-1">
                          <span>{conv.participant.name}</span>
                          {conv.participant.is_verified && (
                            <BadgeCheck className="w-3 h-3 text-blue-500 flex-shrink-0" />
                          )}
                        </p>
                        {/* Last Message Preview */}
                        <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                          {conv.last_message.sender_id === user.id && (
                            <span className="text-muted-foreground mr-1">You:</span>
                          )}
                          {conv.last_message.content}
                        </p>
                      </div>
                      
                      {/* Archive Button (visible on hover) */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleArchiveConversation(conv.id, conv.is_archived);
                        }}
                        className="z-20 opacity-0 group-hover:opacity-100 flex-shrink-0 p-2 rounded-lg bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground transition-all"
                        title={conv.is_archived ? 'Uit archief halen' : 'Archiveren'}
                        data-testid={`archive-conversation-${conv.id}`}
                      >
                        {conv.is_archived ? (
                          <Inbox className="w-4 h-4" />
                        ) : (
                          <Archive className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
              {selectedConversation && loadingChat ? (
                <ChatSkeleton />
              ) : selectedConversation && conversationInfo ? (
                <>
                  {/* Chat Header with Product Context */}
                  <div className="border-b border-border">
                    {/* Product Card - Compact sticky context bar */}
                    <div className="px-4 py-2 bg-secondary/50 border-b border-border/50">
                      <button
                        onClick={() => navigate(`/listing/${conversationInfo.listing.id}`)}
                        className="w-full flex items-center gap-3 group"
                        data-testid="chat-listing-card"
                      >
                        {/* Listing Thumbnail */}
                        {conversationInfo.listing.image ? (
                          <img
                            src={conversationInfo.listing.image}
                            alt={conversationInfo.listing.title}
                            className="w-12 h-12 rounded-lg object-cover bg-secondary flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                            <Package className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                        
                        {/* Listing Info */}
                        <div className="flex-1 min-w-0 text-left">
                          <p className="font-medium text-sm text-foreground truncate group-hover:text-primary transition-colors">
                            {conversationInfo.listing.title}
                          </p>
                          <p className="text-sm text-primary font-semibold">
                            {formatPrice(conversationInfo.listing.price, 'USD')}
                          </p>
                        </div>
                        
                        {/* View Listing Button */}
                        <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors flex items-center gap-1 flex-shrink-0">
                          View listing
                          <ArrowLeft className="w-3 h-3 rotate-180" />
                        </span>
                      </button>
                    </div>
                    
                    {/* User Info Row */}
                    <div className="p-3 flex items-center gap-3">
                      <button
                        onClick={() => setSelectedConversation(null)}
                        className="md:hidden p-2 hover:bg-secondary rounded-lg"
                      >
                        <ArrowLeft className="w-5 h-5" />
                      </button>
                      
                      {/* User Avatar with Online Status */}
                      <div className="relative flex-shrink-0">
                        {conversationInfo.participant.avatar ? (
                          <img
                            src={conversationInfo.participant.avatar}
                            alt={conversationInfo.participant.name}
                            className="w-9 h-9 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-4 h-4 text-primary" />
                          </div>
                        )}
                        {otherUserOnline && (
                          <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full ring-2 ring-card" title="Online" />
                        )}
                      </div>
                      
                      {/* User Name & Status */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-sm text-foreground">
                            {conversationInfo.participant.name}
                          </span>
                          {conversationInfo.participant.is_verified && (
                            <BadgeCheck className="w-4 h-4 text-blue-500" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          {typingUsers[conversationInfo.participant.id] ? (
                            <span className="text-primary animate-pulse">typing...</span>
                          ) : otherUserOnline ? (
                            <span className="text-green-500">Online</span>
                          ) : (
                            <>
                              <Clock className="w-3 h-3" />
                              Typically responds {conversationInfo.participant.response_time}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 min-w-0">
                    <div className="flex flex-col gap-4 min-w-0">
                    {messages.map((msg, index) => {
                      const isOwn = msg.sender_id === user.id;
                      const showDate = index === 0 || 
                        new Date(msg.created_at).toDateString() !== new Date(messages[index - 1].created_at).toDateString();
                      const isTradeProposal = msg.message_type === 'trade_proposal';
                      const isTradeResponse = msg.message_type === 'trade_response';
                      const isTradeCounter = msg.message_type === 'trade_counter';
                      const isTradeAccepted = isTradeResponse && msg.content?.includes('ACCEPTED');
                      
                      return (
                        <div key={msg.id}>
                          {showDate && (
                            <div className="text-center my-4">
                              <span className="text-xs text-muted-foreground bg-secondary px-3 py-1 rounded-full">
                                {new Date(msg.created_at).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          )}
                          <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} min-w-0`}>
                            {isTradeProposal ? (
                              <div className="max-w-[85%] p-4 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                                    <ArrowLeftRight className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="font-semibold text-emerald-600 dark:text-emerald-400 text-sm">Trade Proposal</span>
                                </div>
                                <p className="text-sm whitespace-pre-wrap break-all overflow-hidden text-foreground" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</p>
                                <Link 
                                  to="/trade-proposals" 
                                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-colors"
                                >
                                  <Package className="w-4 h-4" />
                                  Accept or Decline
                                </Link>
                                <div className="flex items-center justify-end gap-1 mt-2 text-muted-foreground">
                                  <span className="text-[10px]">
                                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                              </div>
                            ) : isTradeResponse ? (
                              <div className={`max-w-[85%] p-4 rounded-2xl ${
                                isTradeAccepted 
                                  ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30'
                                  : 'bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30'
                              }`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                    isTradeAccepted 
                                      ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                                      : 'bg-gradient-to-br from-red-500 to-orange-600'
                                  }`}>
                                    {isTradeAccepted 
                                      ? <CheckCircle className="w-4 h-4 text-white" />
                                      : <XCircle className="w-4 h-4 text-white" />
                                    }
                                  </div>
                                  <span className={`font-semibold text-sm ${
                                    isTradeAccepted ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                  }`}>
                                    {isTradeAccepted ? 'Trade Accepted!' : 'Trade Declined'}
                                  </span>
                                </div>
                                <p className="text-sm whitespace-pre-wrap break-all overflow-hidden text-foreground" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</p>
                                <div className="flex items-center justify-end gap-1 mt-2 text-muted-foreground">
                                  <span className="text-[10px]">
                                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                              </div>
                            ) : isTradeCounter ? (
                              <div className="max-w-[85%] p-4 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                                    <RotateCcw className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="font-semibold text-amber-600 dark:text-amber-400 text-sm">Counter Offer</span>
                                </div>
                                <p className="text-sm whitespace-pre-wrap break-all overflow-hidden text-foreground" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</p>
                                <Link 
                                  to="/trade-proposals" 
                                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
                                >
                                  <Package className="w-4 h-4" />
                                  View Counter Offer
                                </Link>
                                <div className="flex items-center justify-end gap-1 mt-2 text-muted-foreground">
                                  <span className="text-[10px]">
                                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div
                                className={`max-w-[75%] p-3 rounded-2xl min-w-0 overflow-hidden ${
                                  isOwn
                                    ? 'bg-primary text-white rounded-br-md'
                                    : 'bg-secondary text-foreground rounded-bl-md'
                                }`}
                              >
                                {msg.image_url && (
                                  <div 
                                    className="relative group mb-2 cursor-pointer"
                                    onClick={() => setImageLightbox(msg.image_url)}
                                  >
                                    <img 
                                      src={msg.image_url} 
                                      alt="Attached" 
                                      className="max-w-full rounded-lg hover:opacity-90 transition-opacity"
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg">
                                      <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                  </div>
                                )}
                                <p className="text-sm whitespace-pre-wrap break-all overflow-hidden" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</p>
                                
                                {/* Translation Section */}
                                {translations[msg.id]?.visible && translations[msg.id]?.text && (
                                  <div className={`mt-2 pt-2 border-t ${isOwn ? 'border-white/20' : 'border-border'}`}>
                                    <p className={`text-sm whitespace-pre-wrap break-all overflow-hidden italic ${isOwn ? 'text-white/90' : 'text-foreground/90'}`} style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                      {translations[msg.id].text}
                                    </p>
                                    <span className={`text-[9px] ${isOwn ? 'text-white/50' : 'text-muted-foreground'}`}>
                                      {t('translated_from') || 'Translated from'} {translations[msg.id].source_language?.toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                
                                <div className={`flex items-center justify-between gap-2 mt-1 ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`}>
                                  {/* Translate Button - Only show for other user's messages */}
                                  {!isOwn && !msg.id?.startsWith('temp-') && (
                                    <button
                                      onClick={() => translateMessage(msg.id)}
                                      className={`flex items-center gap-1 text-[10px] hover:opacity-80 transition-opacity ${
                                        translations[msg.id]?.visible ? 'opacity-100' : 'opacity-60'
                                      }`}
                                      title={t('translate') || 'Translate'}
                                    >
                                      {translations[msg.id]?.loading ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <Languages className="w-3 h-3" />
                                      )}
                                      <span>{translations[msg.id]?.visible ? (t('hide') || 'Hide') : (t('translate') || 'Translate')}</span>
                                    </button>
                                  )}
                                  {isOwn && <div />}
                                  
                                  <div className="flex items-center gap-1">
                                    <span className="text-[10px]">
                                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    {isOwn && (
                                      msg.read ? (
                                        <CheckCheck className="w-3 h-3" />
                                      ) : (
                                        <Check className="w-3 h-3" />
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    </div>
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <form onSubmit={sendMessage} className="p-4 border-t border-border relative">
                    {/* Photo Preview */}
                    {chatPhotoPreview && (
                      <div className="mb-3 relative inline-block">
                        <img 
                          src={chatPhotoPreview} 
                          alt="Preview" 
                          className="h-20 w-auto rounded-lg border border-border"
                        />
                        <button
                          type="button"
                          onClick={removePhoto}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    {/* Attachment Menu Popup - Desktop */}
                    {showMobileAttachMenu && (
                      <div className="absolute bottom-20 left-4 bg-card border border-border rounded-xl shadow-xl p-2 z-10 min-w-[220px]">
                        <button
                          type="button"
                          onClick={() => {
                            photoInputRef.current?.click();
                            setShowMobileAttachMenu(false);
                          }}
                          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
                        >
                          <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                            <ImagePlus className="w-5 h-5 text-blue-500" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-foreground">Photo</p>
                            <p className="text-xs text-muted-foreground">Send an image</p>
                          </div>
                        </button>
                        
                        {user?.usdt_erc20_address && (
                          <button
                            type="button"
                            onClick={() => {
                              setNewMessage(prev => prev + (prev ? '\n' : '') + `💰 My USDT (ERC20) Address:\n${user.usdt_erc20_address}`);
                              toast.success('USDT address added to message');
                              setShowMobileAttachMenu(false);
                            }}
                            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
                          >
                            <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                              <Wallet className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-foreground">USDT Address</p>
                              <p className="text-xs text-muted-foreground">Share your wallet address</p>
                            </div>
                          </button>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      {/* Hidden file input */}
                      <input
                        type="file"
                        ref={photoInputRef}
                        onChange={handlePhotoSelect}
                        accept="image/*,.heic,.heif"
                        className="hidden"
                      />
                      
                      {/* Plus button for attachment menu */}
                      <button
                        type="button"
                        onClick={() => setShowMobileAttachMenu(!showMobileAttachMenu)}
                        className={`p-3 rounded-xl transition-all ${
                          showMobileAttachMenu 
                            ? 'bg-primary text-white rotate-45' 
                            : 'bg-secondary hover:bg-secondary/80 text-muted-foreground'
                        }`}
                        title="Attachments"
                        data-testid="desktop-attach-menu"
                      >
                        <Plus className="w-5 h-5 transition-transform" />
                      </button>
                      
                      <input
                        ref={inputRef}
                        type="text"
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          handleTyping();
                        }}
                        onFocus={() => setShowMobileAttachMenu(false)}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-3 bg-secondary rounded-xl text-sm focus:ring-2 focus:ring-primary/50 outline-none"
                        data-testid="message-input"
                      />
                      <button
                        type="submit"
                        disabled={(!newMessage.trim() && !chatPhoto) || sending}
                        className="p-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="send-message-btn"
                      >
                        {sending || uploadingPhoto ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageCircle className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h2 className="text-lg font-semibold text-foreground mb-2">Select a conversation</h2>
                    <p className="text-muted-foreground text-sm">
                      Choose from your existing conversations or start a new one
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DashboardContainer>

      {/* Image Lightbox Modal */}
      {imageLightbox && (
        <div 
          className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
          onClick={() => setImageLightbox(null)}
          data-testid="image-lightbox"
        >
          <button 
            onClick={() => setImageLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img 
            src={imageLightbox} 
            alt="Full size" 
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
